/**
 * New Year tattoos — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_seasons`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const newYearTattoosPack: ToilePack = {
    slug: 'new-year-tattoos',
    category: 'season',
    name: 'New Year tattoos',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'clock', label: 'Clock', subject: 'A small pocket-watch face at midnight with hour and minute hands'},
        {key: 'firework', label: 'Firework', subject: 'A single firework burst with radiating sparks and trailing stars'},
        {key: 'glass', label: 'Tiny glass', subject: 'A tiny champagne coupe with rising bubbles'},
    ],
    styles: [
        {key: 'simple', label: 'Simple lines', style: 'bold single-weight black outline drawing, large open shapes, clean closed line art', palette: 'Black ink only', framing: 'Centered on a pure white page with a white margin, the full page used'},
        {key: 'detailed', label: 'Detailed', style: 'intricate fine black line art, dense decorative detail, clean closed line art', palette: 'Black ink only', framing: 'Filling the page edge to edge inside a thin white margin'},
        {key: 'doodle', label: 'Doodle', style: 'hand-drawn marker doodle line art, loose confident strokes, clean closed line art', palette: 'Black ink only', framing: 'Scattered across the page with a white margin'},
    ],
    directions: ['a star instead', 'as a matching pair', 'smaller and minimal'],
    directionHint: 'try: a star instead',
    seo: {
        title: 'New Year tattoos: clock, firework, glass',
        metaDescription: 'Nine New Year tattoo designs — a midnight clock, a firework, a tiny champagne glass — in simple, detailed or doodle line weight, no skin.',
        h1: 'New Year tattoo designs: clock, firework and champagne glass',
        tagline: 'Nine designs, three fresh-start motifs, three line weights each — drawn as a tattoo, not a picture.',
        intro: 'A fresh-start tattoo drawn as a tattoo, not a picture: a small pocket-watch face at midnight, a single firework burst, a tiny champagne coupe with rising bubbles, each in bold simple line, intricate detailed line, or a looser doodle weight. Isolated on white with no skin or arm, so it reads as a reference. Ask for a different motif, like a star, and it redraws in the same three line weights.',
        useCases: [
            'a stencil reference for a fresh-start design',
            'a print for comparing line weights',
            'a reference sheet for a tattoo consultation',
            'a starting point for a matching-pair design',
        ],
        faq: [
            {q: 'What size do these print at?', a: 'Each design is centered on a square page with a wide white margin, small tattoo-flash scale.'},
            {q: 'Are these ready to tattoo?', a: 'They are designs, not stencils — an artist redraws the outline at placement size before transferring it.'},
            {q: 'Can I get a different fresh-start motif?', a: "Yes — that's what the direction field is for. Type a different motif, like a star, and it redraws in the same three line weights."},
            {q: "What's the difference between the three line weights?", a: 'Simple is a bold open outline, detailed adds fine decorative line work, and doodle is a looser hand-drawn stroke — three ways the same motif reads as a tattoo.'},
        ],
    },
}
